import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import AvailabilityWidget from '../components/AvailabilityWidget';
import { getDurationOptions, getOccupancyOptions } from '../../search/static/staticOptions';
import { RootState } from '../../../config/redux/types';
import { Accommodation, Property } from '../../../routes/reducers/types';
import { useFetchBookings } from '../api/bookingsApi';
import { useFetchPrices } from '../../../features/prices/priceApi';
import { useEffectiveSelection } from '../effectiveSelectionHook';

interface AvailabilityWidgetProps {
  property: Property;
  accommodation: Accommodation;
}

function isGoogleBot(): boolean {
  if (typeof window === 'undefined' || typeof window.navigator === 'undefined') {
    return false;
  }
  return window.navigator.userAgent.indexOf('Googlebot') > -1;
}

export default function AvailabilityWidgetContainer({
  property,
  accommodation,
}: AvailabilityWidgetProps): JSX.Element | null {
  const maxOccupancy = accommodation.max_persons;
  const lang = useSelector((state: RootState) => state.routes.locale);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchArrival = searchParams.get('arrival');
  const searchDurationOfStay = searchParams.get('duration_of_stay');
  const searchOccupancy = searchParams.get('max_occupancy');
  const [currentArrival, setCurrentArrival] = useState(
    searchArrival != null ? moment(searchArrival, 'YYYY-MM-DD') : null,
  );
  const [currentDurationOfStay, setCurrentDurationOfStay] = useState(
    searchDurationOfStay != null ? parseInt(searchDurationOfStay, 10) : undefined,
  );
  const [currentOccupancy, setCurrentOccupancy] = useState(
    searchOccupancy != null ? parseInt(searchOccupancy, 10) : maxOccupancy,
  );
  const firstRender = useRef(true);

  function getValidOccupancy(occupancy: number): number {
    if (occupancy < 1) {
      return 1;
    }
    return occupancy <= accommodation.max_persons ? occupancy : accommodation.max_persons;
  }

  useEffect(() => {
    // Do not redirect from the canonical URL for Google Bot
    if (isGoogleBot() && firstRender.current) {
      firstRender.current = false;
      return;
    }
    firstRender.current = false;

    const search = new URLSearchParams();
    if (currentArrival) {
      search.set('arrival', currentArrival.format('YYYY-MM-DD'));
    }
    if (currentDurationOfStay) {
      search.set('duration_of_stay', currentDurationOfStay.toString());
    }
    if (currentOccupancy) {
      search.set('max_occupancy', currentOccupancy.toString());
    }
    setSearchParams(search, { replace: true });
  }, [currentArrival, currentDurationOfStay, currentOccupancy]);

  const { data: bookingsData, error } = useFetchBookings(lang, property.id, currentDurationOfStay);
  const bookings = bookingsData?.calendar;
  const minStay = bookingsData?.minStay;

  const { data: pricesData, error: pricesError } = useFetchPrices(
    lang,
    accommodation.id,
    true,
    currentArrival?.toDate(),
    currentDurationOfStay,
    currentOccupancy,
  );

  const { effectiveArrival, selectedBooking, effectiveDurationOfStay, effectiveOccupancy } =
    useEffectiveSelection(
      pricesData,
      bookings,
      currentArrival,
      currentDurationOfStay,
      currentOccupancy,
      maxOccupancy,
    );

  if (effectiveArrival != null && currentArrival == null) {
    setCurrentArrival(effectiveArrival);
  }

  if (effectiveDurationOfStay != null && currentDurationOfStay == null) {
    setCurrentDurationOfStay(effectiveDurationOfStay);
  }

  if (effectiveOccupancy != null && currentOccupancy == null) {
    setCurrentOccupancy(getValidOccupancy(effectiveOccupancy));
  }

  if (currentOccupancy > accommodation.max_persons) {
    setCurrentOccupancy(getValidOccupancy(accommodation.max_persons));
  }

  return (
    <AvailabilityWidget
      bookings={bookings || {}}
      accommodation={accommodation}
      options={{
        durations: getDurationOptions(minStay),
        occupancies: getOccupancyOptions(maxOccupancy),
      }}
      durationOfStay={currentDurationOfStay}
      occupancy={currentOccupancy}
      arrival={effectiveArrival}
      isAbsolutePrice={!!pricesData?.hasMatchingPrice}
      isPriceLoading={!pricesData}
      isPriceError={pricesError != null}
      booking={selectedBooking}
      selectedPrice={pricesData}
      onDurationChange={(option: { value: number }): void => {
        setCurrentDurationOfStay(option.value);
      }}
      onOccupancyChange={(option: { value: number }): void => {
        setCurrentOccupancy(option.value);
      }}
      onDateChange={(value: moment.Moment): void => {
        setCurrentArrival(value);
      }}
    />
  );
}
